// Retro-styled form components for CRT terminal aesthetic
import React from 'react';
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    FormControlLabel,
    InputLabel,
    Checkbox,
    Switch,
    Button,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    InputAdornment,
    Box,
    Typography,
} from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';

// Common retro styling constants
const RETRO_COLORS = {
    gold: '#b28c50',
    teal: '#5fd4c4',
    amber: '#f5a623',
    green: '#00ff41',
    background: 'rgba(0, 0, 0, 0.4)',
    backgroundHover: 'rgba(0, 0, 0, 0.5)',
    border: 'rgba(63, 106, 100, 0.4)',
    borderFocus: 'rgba(95, 212, 196, 0.6)',
    text: '#c8c8c8',
    textMuted: '#888',
    error: '#e74c3c',
};

const baseInputSx = {
    '& .MuiOutlinedInput-root': {
        fontFamily: vortxTypography.mono,
        fontSize: '0.85rem',
        color: RETRO_COLORS.text,
        backgroundColor: RETRO_COLORS.background,
        borderRadius: 0,
        '& fieldset': {
            borderColor: RETRO_COLORS.border,
            borderWidth: '1px',
        },
        '&:hover fieldset': {
            borderColor: RETRO_COLORS.borderFocus,
        },
        '&.Mui-focused fieldset': {
            borderColor: RETRO_COLORS.teal,
            borderWidth: '1px',
            boxShadow: `0 0 8px ${RETRO_COLORS.teal}40`,
        },
        '&.Mui-error fieldset': {
            borderColor: RETRO_COLORS.error,
        },
    },
    '& .MuiInputLabel-root': {
        fontFamily: vortxTypography.mono,
        fontSize: '0.75rem',
        color: RETRO_COLORS.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        '&.Mui-focused': {
            color: RETRO_COLORS.teal,
        },
        '&.Mui-error': {
            color: RETRO_COLORS.error,
        },
    },
    '& .MuiInputBase-input': {
        padding: '10px 12px',
    },
    '& .MuiInputAdornment-root': {
        color: RETRO_COLORS.textMuted,
        '& .MuiTypography-root': {
            fontFamily: vortxTypography.mono,
            fontSize: '0.75rem',
        },
    },
    '& .MuiFormHelperText-root': {
        fontFamily: vortxTypography.mono,
        fontSize: '0.65rem',
        color: RETRO_COLORS.textMuted,
        marginTop: '4px',
        '&.Mui-error': {
            color: RETRO_COLORS.error,
        },
    },
};

// Retro TextField
export const RetroTextField = ({ sx = {}, ...props }) => (
    <TextField
        variant="outlined"
        size="small"
        {...props}
        sx={{
            ...baseInputSx,
            ...sx,
        }}
    />
);

// Retro Select with FormControl
export const RetroSelect = ({
    label,
    value,
    onChange,
    children,
    fullWidth = true,
    size = 'small',
    error,
    helperText,
    sx = {},
    ...props
}) => (
    <FormControl
        fullWidth={fullWidth}
        size={size}
        error={error}
        sx={{
            '& .MuiOutlinedInput-root': {
                fontFamily: vortxTypography.mono,
                fontSize: '0.85rem',
                color: RETRO_COLORS.text,
                backgroundColor: RETRO_COLORS.background,
                borderRadius: 0,
                '& fieldset': {
                    borderColor: RETRO_COLORS.border,
                    borderWidth: '1px',
                },
                '&:hover fieldset': {
                    borderColor: RETRO_COLORS.borderFocus,
                },
                '&.Mui-focused fieldset': {
                    borderColor: RETRO_COLORS.teal,
                    borderWidth: '1px',
                    boxShadow: `0 0 8px ${RETRO_COLORS.teal}40`,
                },
            },
            '& .MuiInputLabel-root': {
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                color: RETRO_COLORS.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                '&.Mui-focused': {
                    color: RETRO_COLORS.teal,
                },
            },
            '& .MuiSelect-icon': {
                color: RETRO_COLORS.textMuted,
            },
            ...sx,
        }}
    >
        {label && <InputLabel>{label}</InputLabel>}
        <Select value={value} onChange={onChange} label={label} {...props}>
            {children}
        </Select>
        {helperText && (
            <Typography
                sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    color: error ? RETRO_COLORS.error : RETRO_COLORS.textMuted,
                    mt: 0.5,
                    ml: 1.5,
                }}
            >
                {helperText}
            </Typography>
        )}
    </FormControl>
);

// Retro MenuItem for use in selects
export const RetroMenuItem = ({ sx = {}, ...props }) => (
    <MenuItem
        {...props}
        sx={{
            fontFamily: vortxTypography.mono,
            fontSize: '0.85rem',
            color: RETRO_COLORS.text,
            '&:hover': {
                backgroundColor: `${RETRO_COLORS.teal}20`,
            },
            '&.Mui-selected': {
                backgroundColor: `${RETRO_COLORS.teal}30`,
                '&:hover': {
                    backgroundColor: `${RETRO_COLORS.teal}40`,
                },
            },
            ...sx,
        }}
    />
);

// Retro Checkbox
export const RetroCheckbox = ({ label, sx = {}, ...props }) => (
    <FormControlLabel
        control={
            <Checkbox
                {...props}
                sx={{
                    color: RETRO_COLORS.border,
                    '&.Mui-checked': {
                        color: RETRO_COLORS.teal,
                    },
                    '&:hover': {
                        backgroundColor: `${RETRO_COLORS.teal}10`,
                    },
                    ...sx,
                }}
            />
        }
        label={label}
        sx={{
            '& .MuiFormControlLabel-label': {
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                color: RETRO_COLORS.text,
            },
        }}
    />
);

// Retro Switch
export const RetroSwitch = ({ label, sx = {}, ...props }) => (
    <FormControlLabel
        control={
            <Switch
                {...props}
                sx={{
                    '& .MuiSwitch-switchBase': {
                        color: RETRO_COLORS.textMuted,
                        '&.Mui-checked': {
                            color: RETRO_COLORS.teal,
                            '& + .MuiSwitch-track': {
                                backgroundColor: RETRO_COLORS.teal,
                                opacity: 0.5,
                            },
                        },
                    },
                    '& .MuiSwitch-track': {
                        backgroundColor: RETRO_COLORS.border,
                    },
                    ...sx,
                }}
            />
        }
        label={label}
        sx={{
            '& .MuiFormControlLabel-label': {
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                color: RETRO_COLORS.text,
            },
        }}
    />
);

// Retro Button
export const RetroButton = ({
    variant = 'outlined',
    color = 'primary',
    sx = {},
    children,
    ...props
}) => {
    const getColorStyles = () => {
        switch (color) {
            case 'primary':
                return {
                    borderColor: RETRO_COLORS.gold,
                    color: RETRO_COLORS.gold,
                    hoverBg: `${RETRO_COLORS.gold}20`,
                    hoverBorder: RETRO_COLORS.gold,
                    containedBg: RETRO_COLORS.gold,
                    containedText: '#1a1a1a',
                };
            case 'secondary':
                return {
                    borderColor: RETRO_COLORS.teal,
                    color: RETRO_COLORS.teal,
                    hoverBg: `${RETRO_COLORS.teal}20`,
                    hoverBorder: RETRO_COLORS.teal,
                    containedBg: RETRO_COLORS.teal,
                    containedText: '#1a1a1a',
                };
            case 'error':
                return {
                    borderColor: RETRO_COLORS.error,
                    color: RETRO_COLORS.error,
                    hoverBg: `${RETRO_COLORS.error}20`,
                    hoverBorder: RETRO_COLORS.error,
                    containedBg: RETRO_COLORS.error,
                    containedText: '#fff',
                };
            default:
                return {
                    borderColor: RETRO_COLORS.border,
                    color: RETRO_COLORS.text,
                    hoverBg: RETRO_COLORS.backgroundHover,
                    hoverBorder: RETRO_COLORS.borderFocus,
                    containedBg: RETRO_COLORS.textMuted,
                    containedText: '#1a1a1a',
                };
        }
    };

    const colors = getColorStyles();

    return (
        <Button
            variant={variant}
            {...props}
            sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                borderRadius: 0,
                padding: '8px 16px',
                ...(variant === 'outlined' && {
                    borderColor: colors.borderColor,
                    color: colors.color,
                    borderWidth: '1px',
                    '&:hover': {
                        borderColor: colors.hoverBorder,
                        backgroundColor: colors.hoverBg,
                        borderWidth: '1px',
                        boxShadow: `0 0 10px ${colors.borderColor}40`,
                    },
                }),
                ...(variant === 'contained' && {
                    backgroundColor: colors.containedBg,
                    color: colors.containedText,
                    border: 'none',
                    '&:hover': {
                        backgroundColor: colors.containedBg,
                        opacity: 0.9,
                        boxShadow: `0 0 15px ${colors.containedBg}60`,
                    },
                }),
                ...(variant === 'text' && {
                    color: colors.color,
                    '&:hover': {
                        backgroundColor: colors.hoverBg,
                    },
                }),
                '&.Mui-disabled': {
                    borderColor: RETRO_COLORS.border,
                    color: RETRO_COLORS.textMuted,
                    opacity: 0.5,
                },
                ...sx,
            }}
        >
            {children}
        </Button>
    );
};

// Retro IconButton
export const RetroIconButton = ({ color = 'default', sx = {}, ...props }) => {
    const getColor = () => {
        switch (color) {
            case 'primary': return RETRO_COLORS.gold;
            case 'secondary': return RETRO_COLORS.teal;
            case 'error': return RETRO_COLORS.error;
            default: return RETRO_COLORS.textMuted;
        }
    };

    return (
        <IconButton
            {...props}
            sx={{
                color: getColor(),
                borderRadius: 0,
                '&:hover': {
                    backgroundColor: `${getColor()}20`,
                },
                ...sx,
            }}
        />
    );
};

// Retro ToggleButtonGroup
export const RetroToggleButtonGroup = ({ sx = {}, children, ...props }) => (
    <ToggleButtonGroup
        {...props}
        sx={{
            '& .MuiToggleButton-root': {
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: RETRO_COLORS.textMuted,
                borderColor: RETRO_COLORS.border,
                borderRadius: 0,
                padding: '6px 16px',
                '&:hover': {
                    backgroundColor: `${RETRO_COLORS.teal}15`,
                },
                '&.Mui-selected': {
                    backgroundColor: `${RETRO_COLORS.teal}25`,
                    color: RETRO_COLORS.teal,
                    borderColor: RETRO_COLORS.teal,
                    '&:hover': {
                        backgroundColor: `${RETRO_COLORS.teal}35`,
                    },
                },
            },
            ...sx,
        }}
    >
        {children}
    </ToggleButtonGroup>
);

// Retro Section Card
export const RetroSectionCard = ({ title, icon: Icon, children, color = RETRO_COLORS.gold, sx = {} }) => (
    <Box
        sx={{
            background: RETRO_COLORS.background,
            border: `1px solid ${RETRO_COLORS.border}`,
            padding: 2,
            mb: 2,
            ...sx,
        }}
    >
        {title && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {Icon && <Icon sx={{ fontSize: 16, color: color }} />}
                <Typography
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        color: color,
                    }}
                >
                    {title}
                </Typography>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: `${color}40`, ml: 1 }} />
            </Box>
        )}
        {children}
    </Box>
);

// Retro Form Group (for organizing form fields)
export const RetroFormGroup = ({ label, children, sx = {} }) => (
    <Box sx={{ mb: 2, ...sx }}>
        {label && (
            <Typography
                sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: RETRO_COLORS.textMuted,
                    mb: 1,
                }}
            >
                {label}
            </Typography>
        )}
        {children}
    </Box>
);

// Retro Alert
export const RetroAlert = ({ severity = 'info', children, sx = {} }) => {
    const getColors = () => {
        switch (severity) {
            case 'error':
                return { bg: `${RETRO_COLORS.error}15`, border: RETRO_COLORS.error, text: RETRO_COLORS.error };
            case 'warning':
                return { bg: `${RETRO_COLORS.amber}15`, border: RETRO_COLORS.amber, text: RETRO_COLORS.amber };
            case 'success':
                return { bg: `${RETRO_COLORS.green}10`, border: RETRO_COLORS.green, text: RETRO_COLORS.green };
            default:
                return { bg: `${RETRO_COLORS.teal}15`, border: RETRO_COLORS.teal, text: RETRO_COLORS.teal };
        }
    };

    const colors = getColors();

    return (
        <Box
            sx={{
                backgroundColor: colors.bg,
                border: `1px solid ${colors.border}`,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                ...sx,
            }}
        >
            <Typography
                sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.8rem',
                    color: colors.text,
                    lineHeight: 1.5,
                }}
            >
                {children}
            </Typography>
        </Box>
    );
};

// Retro Chip/Badge
export const RetroChip = ({ label, color = 'default', sx = {} }) => {
    const getColors = () => {
        switch (color) {
            case 'primary':
                return { bg: `${RETRO_COLORS.gold}25`, text: RETRO_COLORS.gold, border: RETRO_COLORS.gold };
            case 'secondary':
                return { bg: `${RETRO_COLORS.teal}25`, text: RETRO_COLORS.teal, border: RETRO_COLORS.teal };
            case 'success':
                return { bg: `${RETRO_COLORS.green}15`, text: RETRO_COLORS.green, border: RETRO_COLORS.green };
            case 'error':
                return { bg: `${RETRO_COLORS.error}25`, text: RETRO_COLORS.error, border: RETRO_COLORS.error };
            case 'warning':
                return { bg: `${RETRO_COLORS.amber}25`, text: RETRO_COLORS.amber, border: RETRO_COLORS.amber };
            default:
                return { bg: RETRO_COLORS.background, text: RETRO_COLORS.textMuted, border: RETRO_COLORS.border };
        }
    };

    const colors = getColors();

    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                backgroundColor: colors.bg,
                border: `1px solid ${colors.border}`,
                fontFamily: vortxTypography.mono,
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: colors.text,
                ...sx,
            }}
        >
            {label}
        </Box>
    );
};

// Helper to get retro input sx for existing MUI components
export const getRetroInputSx = () => baseInputSx;

// Export colors for use elsewhere
export { RETRO_COLORS };

export default {
    RetroTextField,
    RetroSelect,
    RetroMenuItem,
    RetroCheckbox,
    RetroSwitch,
    RetroButton,
    RetroIconButton,
    RetroToggleButtonGroup,
    RetroSectionCard,
    RetroFormGroup,
    RetroAlert,
    RetroChip,
    getRetroInputSx,
    RETRO_COLORS,
};
